/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { IconPlus, IconSearch, IconX } from "@tabler/icons-react";
import GenerateDRSSheet from "./GenerateDRSSheet";
import DRSExpandableList from "./DRSExpandableList";
import CODSettlementView from "./CODSettlementView";
import { useForm } from "react-hook-form";
import RoleToggle from "../../../components/RoleToggle";
import {
  useDRSCreateByAWBMutation,
  useDRSCreateMutation,
  useDRShipmentReturnMutation,
  useDRSUpdateMutation,
  useFetchDRSData,
} from "../../../apis/drsApi";
import toast from "react-hot-toast";
import { usePermission } from "../../../hooks/usePermissionTabs";
import useDebounce from "../../../utilities/useDebounce";
import Pagination from "../../../components/common/Pagination";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { formatDateTime } from "../../../utilities/helper";
import * as XLSX from "xlsx";
import { useDRSSwapGenerateMutation } from "../../../apis/swapGenerateApis";
import { useFetchSwapData } from "../../../apis/drsSwapApi";

import { useLanguage } from "../../../context/LanguageContext";
import { extractErrorMessage } from "../../../utilities/apiErrorHandler";

type ReturnData = {
  [orderId: string]: {
    reasonType: string;
    reason: string;
    images?: File[];
  };
};

const DrsListManagement: React.FC = () => {
  const { t, language } = useLanguage();
  const { allowed: canCreateDrs } = usePermission("drs", "create");
  const { allowed: canReadDrs } = usePermission("drs", "read");
  const { allowed: canReadCod } = usePermission("cod_orders", "read");
  const { allowed: canUpdateDrs } = usePermission("drs", "update");
  const [openDRS, setOpenDRS] = useState<boolean>(false);
  const [activeView, setActiveView] = useState<"drs" | "cod">("drs");

  const roleOptions = useMemo(() => {
    const opts = [];
    if (canReadDrs) opts.push("DRS");
    if (canReadCod) opts.push("COD");
    return opts;
  }, [canReadDrs, canReadCod]);

  useEffect(() => {
    if (!canReadDrs && canReadCod && activeView === "drs") {
      setActiveView("cod");
    } else if (canReadDrs && !canReadCod && activeView === "cod") {
      setActiveView("drs");
    }
  }, [canReadDrs, canReadCod, activeView]);
  const [codStats, setCodStats] = useState<{
    ready: number;
    pendingCod: number;
    exposure: number;
    openCases: number;
  }>({
    ready: 0,
    pendingCod: 0,
    exposure: 0,
    openCases: 0,
  });
  const [openAssign, setOpenAssign] = useState<boolean>(false);
  const [swapRequestData, setSwapRequestData] = useState<any>(null);
  const { data: swapsData, refetch: refetchSwaps } = useFetchSwapData(
    1,
    100,
    "",
  );
  const [drsData, setDRSData] = useState<any>();
  const [drsId, setDRSId] = useState<string>("");
  const [openFlet, setOpenFlet] = useState<boolean>(false);
  const [selectedLines, setSelectedLines] = useState<string[]>([]);
  const [isDownloadMode, setIsDownloadMode] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [expandedDrsId, setExpandedDrsId] = useState<string | null>(null);

  const [swap, setSwap] = useState<boolean>(false);
  const openSwapGenrateModal = () => {
    setSwap(!swap);
  };
  const openFletToggleModal = () => {
    if (openFlet) {
      returnForm.reset({ returns: {} });
    }
    setOpenFlet(!openFlet);
  };
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [mode, setMode] = useState("automatic");

  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Generate swap
  const { mutate: generateDRSSwap, isPending: isSwapGeneratePending } =
    useDRSSwapGenerateMutation();

  const handleGenerateSwap = () => {
    if (!drsId) {
      toast.error("DRS ID missing");
      return;
    }

    if (selectedLines.length === 0) {
      toast.error("Please select at least one shipment for swap");
      return;
    }

    const payload = {
      oldDrsId: drsId,
      shipmentIds: selectedLines,
    };

    generateDRSSwap(payload, {
      onSuccess: (data: any) => {
        toast.success("DRS Swap generated successfully");
        setSwap(false);
        setSelectedLines([]);
        refetch();
        refetchSwaps();
      },
      onError: (error: any) => {
        toast.error(extractErrorMessage(error, "Failed to generate swap"));
      },
    });
  };

  const { mutate: createDRS, isPending: isRequestPending } =
    useDRSCreateMutation();
  const { mutate: createDRSByAWB, isPending: isByAWBRequestPending } =
    useDRSCreateByAWBMutation();
  const { mutate: updateDRS, isPending: isUpdateRequestPending } =
    useDRSUpdateMutation(drsData?.id ?? "");

  const {
    mutate: createShipmentReturnMutate,
    isPending: isShipmentReturnPending,
  } = useDRShipmentReturnMutation(drsData?.id ?? "");

  const [savedReturns, setSavedReturns] = useState<ReturnData>({});
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [drsShortId, setDrsShortId] = useState<string>("");

  const debouncedSearchFor = useDebounce(searchFor, 550);

  const {
    data: drs,
    isPending: listPending,
    isFetching,
    refetch,
  } = useFetchDRSData(pageNo, limit, debouncedSearchFor);

  const stats = React.useMemo(() => {
    let activeDrivers = 0;
    let openDrs = 0;
    let delivered = 0;
    let closedDrs = 0;
    let ofdActive = 0;
    let codRisk = 0;
    let riwQueue = 0;

    const uniqueDrivers = new Set();
    const drsList = drs?.drs || [];

    if (Array.isArray(drsList)) {
      drsList.forEach((d) => {
        if (d.driver) uniqueDrivers.add(d.driver);
        if (d.status === "ACTIVE" || d.status === "OPEN") openDrs++;
        if (d.status === "CLOSED") closedDrs++;

        delivered += parseInt(d.ordersDelivered || "0", 10);

        const totalShipments = d.shipments?.length || 0;
        const del = parseInt(d.ordersDelivered || "0", 10);
        const nd = parseInt(d.ordersNotDelivered || "0", 10);
        const ofd = totalShipments - (del + nd);
        ofdActive += ofd > 0 ? ofd : 0;

        // Calculate pending COD risk
        const totalCod = parseInt(d.codAmount || "0", 10);
        const collected = parseInt(d.collectedAmount || "0", 10);
        codRisk += Math.max(0, totalCod - collected);

        // RIW Queue calculation
        const riw = parseInt(d.receiveInWarehouse || "0", 10);
        riwQueue += riw > 0 ? riw : nd;
      });
    }

    activeDrivers = uniqueDrivers.size;

    return {
      activeDrivers,
      openDrs,
      delivered,
      closedDrs,
      ofdActive,
      codRisk,
      riwQueue,
    };
  }, [drs?.drs]);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<any>();

  const openDRSToggleModal = () => {
    reset();
    setOpenDRS(!openDRS);
  };

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    refetch();
    refetchSwaps();
  }, [debouncedSearchFor, pageNo, limit, activeView]);

  useEffect(() => {
    setSelectedRows([]);
    setSelectedLines([]);
  }, [expandedDrsId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCreateDRS = (data: any) => {
    const payload = {
      ...data,
      shipments: data?.selectedOrders,
    };

    if (!data?.routeId) {
      delete payload.routeId;
    }

    createDRS(payload, {
      onSuccess: (res: any) => {
        setOpenDRS(false);
        refetch();

        if (res?.data?.invalidOrders?.length > 0) {
          console.warn("Invalid Orders:", res.data.invalidOrders);

          // Show toast with count
          toast.error(
            `${res.data.invalidOrders
              ?.map((order: any) => order?.awb)
              ?.join(
                ",",
              )} shipments could not be assigned due to route mismatch`,
          );
        }

        toast.success(res?.message || "DRS Created Successfully");
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(extractErrorMessage(error));
      },
    });
  };

  const handleCreateDRSByAWB = (data: any) => {
    const payload = {
      ...data,
      shipments: data?.selectedOrders,
    };

    createDRSByAWB(payload, {
      onSuccess: (res: any) => {
        setOpenDRS(false);
        refetch();

        if (res?.data?.invalidOrders?.length > 0) {
          console.warn("Invalid Orders:", res.data.invalidOrders);

          // Show toast with count
          toast.error(
            `${res.data.invalidOrders
              ?.map((order: any) => order?.awb)
              ?.join(
                ",",
              )} shipments could not be assigned due to route mismatch`,
          );
        }

        toast.success(res?.message || "DRS Created Successfully");
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(extractErrorMessage(error));
      },
    });
  };

  const handleUpdateDRS = (data: any) => {
    const payload = {
      ...data,
      shipments: data?.selectedOrders,
    };
    updateDRS(payload, {
      onSuccess: (data: any) => {
        setOpenDRS(false);
        refetch();
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(extractErrorMessage(error));
      },
    });
  };

  const handleSaveAllReturns = async () => {
    if (selectedRows.length === 0) {
      toast("Please select at least one shipment to return", {
        style: { background: "#f97316", color: "#ffffff" },
        icon: "⚠️",
      });
      return;
    }

    // Find any invalid entries: reasonType selected but reason missing
    const invalidEntries = selectedRows.filter((orderId) => {
      const data = savedReturns[orderId];
      return data?.reasonType && (!data?.reason || !data.reason.trim());
    });

    // Find any DAMAGED without images
    const damagedWithoutImages = selectedRows.filter((orderId) => {
      const data = savedReturns[orderId];
      return (
        data?.reasonType === "DAMAGED" &&
        (!data?.images || data.images.length === 0)
      );
    });

    // Block only if there are incomplete entries
    if (invalidEntries.length > 0 || damagedWithoutImages.length > 0) {
      if (damagedWithoutImages.length > 0) {
        toast("Please upload images for all 'Damaged' shipments", {
          style: { background: "#f97316", color: "#ffffff" },
          icon: "⚠️",
        });
      } else {
        toast(
          "Please provide an explanation for shipments where reason type is selected",
          {
            style: { background: "#f97316", color: "#ffffff" },
            icon: "⚠️",
          },
        );
      }
      return;
    }

    // Collect only valid, complete returns
    const validReturns = selectedRows
      .map((orderId) => {
        const data = savedReturns[orderId];
        if (data?.reasonType && data.reason?.trim()) {
          return {
            drsId: drsData?.id,
            orderId,
            reasonType: data.reasonType,
            reason: data.reason.trim(),
          };
        }
        return {
          drsId: drsData?.id,
          orderId,
        };
      })
      .filter(Boolean);

    // If no complete returns → just close quietly or show soft info
    if (validReturns.length === 0) {
      toast("No return reasons were submitted (none were filled)", {
        style: { background: "#94a3b8", color: "#ffffff" },
        duration: 4000,
      });
      // Still close the modal and clear selection — user expects action
      setOpenFlet(false);
      setSelectedRows([]);
      return;
    }

    // Submit only the valid ones
    try {
      createShipmentReturnMutate(validReturns, {
        onSuccess: () => {
          toast.success(
            `Successfully submitted ${validReturns.length} return(s)!`,
          );
          setOpenFlet(false);
          setSelectedRows([]);
          setSavedReturns({});
          refetch();
        },
        onError: (error: any) => {
          toast.error(extractErrorMessage(error, "Failed to submit returns"));
        },
      });
    } catch (err) {
      console.error(err);
      toast.error("An unexpected error occurred");
    }
  };
  const [columns, setColumns] = useState([
    { id: 1, text: "Checkbox", value: "checkbox", isShow: false },
    { id: 2, text: "S/N", value: "sn", isShow: true },
    { id: 3, text: "DRS Id", value: "drsId", isShow: true },
    { id: 4, text: "Driver", value: "driver", isShow: true },
    { id: 5, text: "City", value: "city", isShow: true },
    { id: 6, text: "Route Code", value: "routeCode", isShow: true },
    { id: 7, text: "Shipments", value: "shipments", isShow: true },
    { id: 8, text: "COD", value: "cod", isShow: true },
    { id: 9, text: "Date & Time", value: "dateTime", isShow: true },
    { id: 10, text: "Status", value: "status", isShow: true },
    { id: 11, text: "Action", value: "action", isShow: true },
  ]);

  const handleSetHeadings = (value: string | undefined, checked: boolean) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const handleDownloadXLSX = () => {
    if (!drs?.drs?.length) {
      toast.error("No data available to download");
      return;
    }

    if (!isDownloadMode) {
      setIsDownloadMode(true);
      setColumns((prev) =>
        prev.map((col) =>
          col.value === "checkbox" ? { ...col, isShow: true } : col,
        ),
      );
      return;
    }

    if (selectedData.length === 0) {
      toast.error("Please select at least one row to download");
      return;
    }

    const visibleColumns = columns.filter(
      (col) => col.isShow && col.value !== "checkbox",
    );

    const dataToExport = selectedData.map((item: any, index: number) => {
      const row: any = {};
      visibleColumns.forEach((col) => {
        if (col.value === "sn") {
          row[col.text] = index + 1;
        } else if (col.value === "drsId") {
          row[col.text] = item.drsId;
        } else if (col.value === "driver") {
          row[col.text] = item.driver;
        } else if (col.value === "city") {
          row[col.text] = item.city;
        } else if (col.value === "routeCode") {
          row[col.text] = item.routeCode;
        } else if (col.value === "shipments") {
          row["Total Shipments"] = item.shipments?.length || 0;
          row["POD"] = item.ordersDelivered || 0;
          row["ND"] = item.ordersNotDelivered || 0;
          row["OFD"] =
            (item.shipments?.length || 0) -
            ((parseInt(item.ordersDelivered) || 0) +
              (parseInt(item.ordersNotDelivered) || 0));
        } else if (col.value === "cod") {
          row["Total COD"] = item.codAmount || 0;
          row["Collected COD"] = parseInt(item.collectedAmount) || 0;
        } else if (col.value === "dateTime") {
          row[col.text] = formatDateTime(item.createdAt);
        } else if (col.value === "status") {
          row[col.text] = item.status;
        }
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 16 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 16 },
      { wch: 24 },
      { wch: 15 },
    ].filter((_, index) => {
      let colIndex = 0;
      return visibleColumns.some((col) => {
        if (col.value === "shipments") {
          colIndex += 4;
          return index < colIndex;
        } else if (col.value === "cod") {
          colIndex += 2;
          return index < colIndex;
        } else {
          colIndex += 1;
          return index < colIndex;
        }
      });
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DRS List");
    XLSX.writeFile(
      workbook,
      `DRS_List_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    setIsDownloadMode(false);
    setSelectedData([]);
    setColumns((prev) =>
      prev.map((col) =>
        col.value === "select" ? { ...col, isShow: false } : col,
      ),
    );
  };

  const onRiw = (item: any, type?: string) => {
    setDRSData(item);
    setDRSId(item?.drsId);
    setOpenFlet(true);
    if (type === "swap") {
      setSwap(true);
    } else {
      setSwap(false);
    }
  };

  // Add this near the top with other hooks
  const returnForm = useForm<{
    returns: Record<
      string,
      {
        reasonType: string;
        reason: string;
        images: File[];
      }
    >;
  }>({
    defaultValues: { returns: {} },
    mode: "onChange",
  });

  // Optional: Add validation rule for images when reasonType is DAMAGED
  // useEffect(() => {
  //   if (returnForm.watch(`returns`)?.[expandedRow]?.reasonType === "DAMAGED") {
  //     returnForm.trigger(`returns.${expandedRow}.images`);
  //   }
  // }, [returnForm.watch(`returns`)]);

  return (
    <>
      <div className="space-y-0">
        {/* Top Header Row with Title, RoleToggle, and Stats Capsule */}
        <div 
          dir={language === "ar" ? "rtl" : "ltr"}
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 "
        >
          <div className="flex items-center gap-4">
            {roleOptions.length > 0 && (
              <RoleToggle
                options={roleOptions}
                active={activeView === "drs" ? "DRS" : "COD"}
                onChange={(val) => setActiveView(val === "DRS" ? "drs" : "cod")}
              />
            )}
          </div>
          {activeView === "drs" && (
            <div 
              dir={language === "ar" ? "rtl" : "ltr"}
              className="flex flex-wrap items-center gap-3 bg-white px-4 py-1.5 rounded-2xl md:rounded-full border border-slate-200 shadow-sm text-xs select-none font-semibold h-fit"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Selected:")}</span>
                <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-md font-bold">
                  {selectedData.length}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Total:")}</span>
                <span className="text-slate-700 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-md font-bold">
                  {drs?.pagination?.total || drs?.drs?.length || 0}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Active Drivers:")}</span>
                <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-md font-bold">
                  {stats.activeDrivers}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Open DRS:")}</span>
                <span className="text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded-md font-bold">
                  {stats.openDrs}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Delivered:")}</span>
                <span className="text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-md font-bold">
                  {stats.delivered}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Wallet Risk:")}</span>
                <span className="text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-md font-bold">
                  {language === "ar" ? `${stats.codRisk} ر.س` : `SAR ${stats.codRisk}`}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("RIW Queue:")}</span>
                <span className="text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded-md font-bold">
                  {stats.riwQueue}
                </span>
              </div>
            </div>
          )}
          {activeView === "cod" && (
            <div 
              dir={language === "ar" ? "rtl" : "ltr"}
              className="flex flex-wrap items-center gap-3 bg-white px-4 py-1.5 rounded-2xl md:rounded-full border border-slate-200 shadow-sm text-xs select-none font-semibold h-fit"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Ready:")}</span>
                <span className="text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-md font-bold">
                  {codStats.ready}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Pending COD:")}</span>
                <span className="text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded-md font-bold">
                  {language === "ar" ? `${codStats.pendingCod} ر.س` : `SAR ${codStats.pendingCod}`}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Exposure:")}</span>
                <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-md font-bold">
                  {language === "ar" ? `${codStats.exposure} ر.س` : `SAR ${codStats.exposure}`}
                </span>
              </div>
              <span className="hidden sm:inline-block h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Open Cases:")}</span>
                <span className="text-slate-700 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-md font-bold">
                  {codStats.openCases}
                </span>
              </div>
            </div>
          )}
        </div>

        {activeView === "drs" && (
          <>
            <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div
                className={
                  openDRS ? "col-span-12 xl:col-span-7" : "col-span-12"
                }
              >
                {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
                <div className="bg-[#ffffff]/20 border rounded-3xl">
                  <div className="px-6 py-1.5 flex flex-col md:flex-row md:items-center justify-between gap-4 select-none">
                    {/* Left side: Brand badge */}
                    <div className="flex flex-wrap items-center gap-3"></div>

                    <div className="flex items-center flex-wrap gap-2.5">
                      <div className="relative flex items-center">
                        <div
                          onClick={() => {
                            if (!showSearch) {
                              setShowSearch(true);
                            }
                          }}
                          className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-md bg-white shadow-sm h-8 shrink-0 ${
                            showSearch
                              ? "w-64 px-2 cursor-default"
                              : "w-8 justify-center cursor-pointer"
                          }`}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSearch(!showSearch);
                            }}
                            className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                            title={t("Search")}
                          >
                            <IconSearch size={17} stroke={2.5} />
                          </button>
                          {showSearch && (
                            <>
                              <input
                                type="text"
                                value={searchFor}
                                onChange={(e) => {
                                  setSearchFor(e.target.value);
                                  setPageNo(1);
                                }}
                                placeholder={t("Search...")}
                                autoFocus
                                className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none border-0 focus:ring-0 focus:outline-none p-0"
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchFor("");
                                  setShowSearch(false);
                                  setPageNo(1);
                                }}
                                className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                                title={t("Clear & Close")}
                              >
                                <IconX size={14} stroke={2.5} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Plus Button */}
                      {canCreateDrs && (
                        <button
                          onClick={() => {
                            setDRSData(null);
                            reset();
                            const nextVal = !openDRS;
                            setOpenDRS(nextVal);
                            if (nextVal) {
                              setOpenFlet(false);
                              setSwap(false);
                              setOpenAssign(false);
                            }
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border border-[#FBD037] bg-[#FBD037] text-[#131327] hover:bg-[#e2bd30] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                          title={t("Create DRS")}
                        >
                          <IconPlus size={17} stroke={2.5} />
                        </button>
                      )}

                      {/* Entries Limit Selector */}
                      {drs && drs.totalPages * limit > 10 && (
                        <EntriesSelector
                          value={limit}
                          options={[
                            10,
                            ...(drs.totalPages * limit > 10 ? [20] : []),
                            ...(drs.totalPages * limit > 20 ? [30] : []),
                            ...(drs.totalPages * limit > 50 ? [50] : []),
                            ...(drs.totalPages * limit > 100 ? [100] : []),
                          ]}
                          onChange={(val) => {
                            setLimit(val);
                            setPageNo(1);
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* ── Table ────────────────────────────────────────────────── */}
                  <div className="border-t border-[#ECECEC]">
                    {listPending ? (
                      <div className="bg-[#F7F6F2] p-4">
                        <TableSkeleton
                          columns={columns
                            .filter((c) => c.isShow)
                            .map((c) => t(c.text))}
                          rows={6}
                        />
                      </div>
                    ) : drs?.drs?.length ? (
                      <div className="flex flex-col lg:flex-row gap-6 bg-[#F7F6F2]  items-start">
                        <DRSExpandableList
                          data={drs?.drs}
                          isFormOpen={openDRS}
                          onRiw={(item) => {
                            setDRSData(item);
                            setDRSId(item?.drsId);
                            setOpenFlet(true);
                            setOpenDRS(false);
                            setSwap(false);
                            setOpenAssign(false);
                          }}
                          OnSwap={(item: any) => {
                            setDRSData(item);
                            setDRSId(item?.id);
                            setDrsShortId(item?.drsId);
                            setSwap(true);
                            setOpenDRS(false);
                            setOpenFlet(false);
                            setOpenAssign(false);
                          }}
                          selectedData={selectedData}
                          setSelectedData={setSelectedData}
                          isDownloadMode={isDownloadMode}
                          onExpandRow={(id) => {
                            setExpandedDrsId(id);
                            if (id) {
                              const found = drs?.drs?.find(
                                (d: any) => d.drsId === id,
                              );
                              if (found) {
                                setDRSData(found);
                                setDRSId(found.id); // DB id
                                setDrsShortId(found.drsId); // Short id
                              }
                            } else {
                              setDRSData(null);
                              setDRSId("");
                              setDrsShortId("");
                            }
                          }}
                          swaps={swapsData?.data || []}
                          onOpenSwapAssign={(swapReq) => {
                            setSwapRequestData(swapReq);
                            setOpenAssign(true);
                            setOpenDRS(false);
                            setOpenFlet(false);
                            setSwap(false);
                          }}
                          // State Props for rendering inside the row
                          openFlet={openFlet}
                          swap={swap}
                          openAssign={openAssign}
                          activeDrsId={drsId}
                          activeDrsShortId={drsShortId}
                          swapRequestData={swapRequestData}
                          // Form & Callback Props
                          returnForm={returnForm}
                          selectedRows={selectedRows}
                          setSelectedRows={setSelectedRows}
                          savedReturns={savedReturns}
                          setSavedReturns={setSavedReturns}
                          isShipmentReturnPending={isShipmentReturnPending}
                          handleSaveAllReturns={handleSaveAllReturns}
                          openFletToggleModal={openFletToggleModal}
                          register={register}
                          errors={errors}
                          clearErrors={clearErrors}
                          setValue={setValue}
                          control={control}
                          watch={watch}
                          selectedLines={selectedLines}
                          setSelectedLines={setSelectedLines}
                          isSwapGeneratePending={isSwapGeneratePending}
                          handleGenerateSwap={handleGenerateSwap}
                          openSwapGenrateModal={openSwapGenrateModal}
                          refetchMainList={refetch}
                          refetchSwapsList={refetchSwaps}
                          onCloseAssign={() => {
                            setOpenAssign(false);
                            setSwapRequestData(null);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col lg:flex-row gap-6 bg-[#F7F6F2] p-4 items-start">
                        <div
                          className={`flex-1 w-full bg-white rounded-[24px] border border-[#ECECEC] p-6 text-center ${openDRS ? "lg:max-w-[calc(100%-524px)]" : ""}`}
                        >
                          <NoDataFound />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Sidebar Right Panel ────────────────────────────────────────── */}
              {openDRS && (
                <>
                  {/* Backdrop on mobile */}

                  <div className="col-span-12 xl:col-span-5 space-y-4   xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
                    <GenerateDRSSheet
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      watch={watch}
                      clearErrors={clearErrors}
                      control={control}
                      drsData={null}
                      mode={mode}
                      setMode={setMode}
                      onSubmit={
                        mode === "scan"
                          ? handleSubmit(handleCreateDRSByAWB)
                          : handleSubmit(handleCreateDRS)
                      }
                      isPending={isRequestPending || isByAWBRequestPending}
                      onClose={() => setOpenDRS(false)}
                    />
                  </div>
                </>
              )}
              {/* ── Pagination ───────────────────────────────────────────── */}
              {drs?.totalPages > 1 && (
                <div className="flex justify-center items-center py-4 bg-white border-t border-[#ECECEC]">
                  <Pagination
                    currentPage={pageNo}
                    totalPages={drs.totalPages}
                    fetchData={handlePaginationFetch}
                  />
                </div>
              )}
            </div>
            {/* <div className="  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300  rounded-r-3xl rounded-b-3xl w-full">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
  
              <div className="px-6 py-3.5 flex flex-col md:flex-row md:items-center justify-between gap-4 select-none">
               
                <div className="flex flex-wrap items-center gap-3"></div> 
                <div className="flex items-center flex-wrap gap-2.5">
 
                  <div className="relative flex items-center">
                    <div
                      onClick={() => {
                        if (!showSearch) {
                          setShowSearch(true);
                        }
                      }}
                      className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-md bg-white shadow-sm h-8 shrink-0 ${
                        showSearch
                          ? "w-64 px-2 cursor-default"
                          : "w-8 justify-center cursor-pointer"
                      }`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSearch(!showSearch);
                        }}
                        className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                        title="Search"
                      >
                        <IconSearch size={17} stroke={2.5} />
                      </button>
                      {showSearch && (
                        <>
                          <input
                            type="text"
                            value={searchFor}
                            onChange={(e) => {
                              setSearchFor(e.target.value);
                              setPageNo(1);
                            }}
                            placeholder="Search..."
                            autoFocus
                            className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none border-0 focus:ring-0 focus:outline-none p-0"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchFor("");
                              setShowSearch(false);
                              setPageNo(1);
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                            title="Clear & Close"
                          >
                            <IconX size={14} stroke={2.5} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
 
                  {canCreateDrs && (
                  <button
                    onClick={() => {
                      setDRSData(null);
                      reset();
                      const nextVal = !openDRS;
                      setOpenDRS(nextVal);
                      if (nextVal) {
                        setOpenFlet(false);
                        setSwap(false);
                        setOpenAssign(false);
                      }
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border border-[#FBD037] bg-[#FBD037] text-[#131327] hover:bg-[#e2bd30] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                    title="Create DRS"
                  >
                    <IconPlus size={17} stroke={2.5} />
                  </button>
                  )}
 
                  {drs && drs.totalPages * limit > 10 && (
                    <EntriesSelector
                      value={limit}
                      options={[
                        10,
                        ...(drs.totalPages * limit > 10 ? [20] : []),
                        ...(drs.totalPages * limit > 20 ? [30] : []),
                        ...(drs.totalPages * limit > 50 ? [50] : []),
                        ...(drs.totalPages * limit > 100 ? [100] : []),
                      ]}
                      onChange={(val) => {
                        setLimit(val);
                        setPageNo(1);
                      }}
                    />
                  )}
                </div>
              </div>
 
              <div className="border-t border-[#ECECEC]">
                {listPending ? (
                  <div className="bg-[#F7F6F2] p-4">
                    <TableSkeleton
                      columns={columns
                        .filter((c) => c.isShow)
                        .map((c) => t(c.text))}
                      rows={6}
                    />
                  </div>
                ) : drs?.drs?.length ? (
                  <div className="flex flex-col lg:flex-row gap-6 bg-[#F7F6F2]  items-start"> 
                    <div
                      className={`flex-1 w-full ${openDRS ? "lg:max-w-[calc(100%-524px)]" : ""}`}
                    >
                      <DRSExpandableList
                        data={drs?.drs}
                        isFormOpen={openDRS}
                        onRiw={(item) => {
                          setDRSData(item);
                          setDRSId(item?.drsId);
                          setOpenFlet(true);
                          setOpenDRS(false);
                          setSwap(false);
                          setOpenAssign(false);
                        }}
                        OnSwap={(item: any) => {
                          setDRSData(item);
                          setDRSId(item?.id);
                          setDrsShortId(item?.drsId);
                          setSwap(true);
                          setOpenDRS(false);
                          setOpenFlet(false);
                          setOpenAssign(false);
                        }}
                        selectedData={selectedData}
                        setSelectedData={setSelectedData}
                        isDownloadMode={isDownloadMode}
                        onExpandRow={(id) => {
                          setExpandedDrsId(id);
                          if (id) {
                            const found = drs?.drs?.find(
                              (d: any) => d.drsId === id,
                            );
                            if (found) {
                              setDRSData(found);
                              setDRSId(found.id);  
                              setDrsShortId(found.drsId);  
                            }
                          } else {
                            setDRSData(null);
                            setDRSId("");
                            setDrsShortId("");
                          }
                        }}
                        swaps={swapsData?.data || []}
                        onOpenSwapAssign={(swapReq) => {
                          setSwapRequestData(swapReq);
                          setOpenAssign(true);
                          setOpenDRS(false);
                          setOpenFlet(false);
                          setSwap(false);
                        }} 
                        openFlet={openFlet}
                        swap={swap}
                        openAssign={openAssign}
                        activeDrsId={drsId}
                        activeDrsShortId={drsShortId}
                        swapRequestData={swapRequestData} 
                        returnForm={returnForm}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        savedReturns={savedReturns}
                        setSavedReturns={setSavedReturns}
                        isShipmentReturnPending={isShipmentReturnPending}
                        handleSaveAllReturns={handleSaveAllReturns}
                        openFletToggleModal={openFletToggleModal}
                        register={register}
                        errors={errors}
                        clearErrors={clearErrors}
                        setValue={setValue}
                        control={control}
                        watch={watch}
                        selectedLines={selectedLines}
                        setSelectedLines={setSelectedLines}
                        isSwapGeneratePending={isSwapGeneratePending}
                        handleGenerateSwap={handleGenerateSwap}
                        openSwapGenrateModal={openSwapGenrateModal}
                        refetchMainList={refetch}
                        refetchSwapsList={refetchSwaps}
                        onCloseAssign={() => {
                          setOpenAssign(false);
                          setSwapRequestData(null);
                        }}
                      />
                    </div>  
                    {openDRS && (
                      <div className="w-full lg:w-[500px] shrink-0 lg:sticky lg:top-4">
                        <GenerateDRSSheet
                          register={register}
                          errors={errors}
                          setValue={setValue}
                          watch={watch}
                          clearErrors={clearErrors}
                          control={control}
                          drsData={null}
                          mode={mode}
                          setMode={setMode}
                          onSubmit={
                            mode === "scan"
                              ? handleSubmit(handleCreateDRSByAWB)
                              : handleSubmit(handleCreateDRS)
                          }
                          isPending={isRequestPending || isByAWBRequestPending}
                          onClose={() => setOpenDRS(false)}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col lg:flex-row gap-6 bg-[#F7F6F2] p-4 items-start">
                    <div
                      className={`flex-1 w-full bg-white rounded-[24px] border border-[#ECECEC] p-6 text-center ${openDRS ? "lg:max-w-[calc(100%-524px)]" : ""}`}
                    >
                      <NoDataFound />
                    </div>
                    {openDRS && (
                      <div className="w-full lg:w-[500px] shrink-0 lg:sticky lg:top-4">
                        <GenerateDRSSheet
                          register={register}
                          errors={errors}
                          setValue={setValue}
                          watch={watch}
                          clearErrors={clearErrors}
                          control={control}
                          drsData={null}
                          mode={mode}
                          setMode={setMode}
                          onSubmit={
                            mode === "scan"
                              ? handleSubmit(handleCreateDRSByAWB)
                              : handleSubmit(handleCreateDRS)
                          }
                          isPending={isRequestPending || isByAWBRequestPending}
                          onClose={() => setOpenDRS(false)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
 
              {drs?.totalPages > 1 && (
                <div className="flex justify-center items-center py-4 bg-white border-t border-[#ECECEC]">
                  <Pagination
                    currentPage={pageNo}
                    totalPages={drs.totalPages}
                    fetchData={handlePaginationFetch}
                  />
                </div>
              )}
            </div> */}
          </>
        )}
        {activeView === "cod" && ( 
            <CODSettlementView drsList={drs?.drs} onStatsUpdate={setCodStats} />
      
        )}
      </div>
    </>
  );
};

export default DrsListManagement;
